import * as React from 'react'
import { Input } from '@mobiscroll/react-latest'
import { IStateMetadata, IOrgObj } from '../../../types'
import { searchLocation } from '../../../utils/location'
import UIMessages from '../../../utils/ui-messages'
import Map from '../Map'
import CreateOrganizationBottomBar from '../create/CreateOrganizationBottomBar'
import FloatingForm from 'src/components/ui/form/FloatingForm'
import { debounce } from 'lodash'

export interface IOrganizationLocationProps {
  metadata?: IStateMetadata
  currentStep: number
  isSaved: boolean
  isSaving: boolean
  organization: IOrgObj
  orgId: string
  isEdit?: boolean
  isCompleted: boolean
  onCompleteStep: (step: number) => void
  onSaveOrganizationLocation: (organization: IOrgObj) => Promise<any>
  showMessage: (error: string, isError: boolean) => void
}

interface IOrganizationLocationState {
  map: any
  autoCompleteLocations: any[]
  buffer: any
  serviceAreaName: string
  serviceAreaBounds: string[]
}

interface ILocation {
  fullAddress: string
  googlePlaceId?: string
}

class OrganizationLocation extends React.Component<IOrganizationLocationProps, IOrganizationLocationState> {
  public debounceAddressChange = debounce(async (address: string) => {
    this.clear()
    const autoCompleteLocations = address ? await searchLocation({ input: address }) : []
    this.setState({
      autoCompleteLocations
    })
  }, 500)

  constructor(props: IOrganizationLocationProps) {
    super(props)

    this.state = {
      map: null,
      buffer: {},
      autoCompleteLocations: [],
      serviceAreaName: this.props.organization.serviceAreaName || '',
      serviceAreaBounds: []
    }
    this.props.showMessage('', false)
  }

  public componentDidUpdate(
    prevProps: Readonly<IOrganizationLocationProps>,
    prevState: Readonly<IOrganizationLocationState>
  ) {
    const { map, serviceAreaName } = this.state
    if (map !== prevState.map && this.props.isEdit && this.state.serviceAreaName) {
      this.onLocationSelect({ fullAddress: serviceAreaName })()
    }
  }

  public render() {
    const mapInnerStyle: any = {
      zIndex: -1,
      display: 'block',
      width: '100%',
      height: '500px'
    }
    const prevStepPath = this.props.isEdit
      ? `/organizations/${this.props.orgId}/edit/organization-subscription`
      : '/organizations/create/organization-subscription'
    const nextStepPath = this.props.isEdit
      ? `/organizations/${this.props.orgId}/edit/legal-entity`
      : '/organizations/create/legal-entity'

    return (
      <div className='org-location-container'>
        <FloatingForm>
          <div className='mbsc-grid' style={{ padding: '0' }}>
            <div className='mbsc-row'>
              <div className='mbsc-col-12 mbsc-col-lg-5 mbsc-padding'>
                <Input
                  inputStyle='box'
                  label='Add State, City or County'
                  labelStyle='floating'
                  name='address'
                  theme='ios'
                  type='text'
                  value={this.state.serviceAreaName}
                  onChange={this.onAddressChange}
                />
                {!!this.state.autoCompleteLocations.length && (
                  <div className='absolute auto-complete-location b-rad-6 bg-grey6 cursor--pointer p2 top-70 wrap z1'>
                    {this.state.autoCompleteLocations.map((item: any) => (
                      <div
                        className='flex fit items-center mb1 on-hover w100'
                        key={item.googlePlaceId}
                        onClick={this.onLocationSelect(item)}
                      >
                        <div>
                          <p className='m0 h5'>{item.fullAddress}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className='mbsc-col-12 mbsc-col-lg-7 mbsc-padding'>
                <div style={mapInnerStyle}>
                  <Map onMapMounted={this.onMapMounted} />
                </div>
              </div>
            </div>
          </div>
        </FloatingForm>
        <CreateOrganizationBottomBar
          isEdit={this.props.isEdit}
          isSaved={!this.props.isEdit && (this.props.isSaved || this.props.isCompleted)}
          isSaving={this.props.isSaving}
          previousStepPath={prevStepPath}
          nextStepPath={nextStepPath}
          onSave={this.onSaveOrganizationLocation}
        />
      </div>
    )
  }

  private onSaveOrganizationLocation = () => {
    if (!this.state.serviceAreaName) {
      this.props.showMessage(UIMessages.organization.organizationInfo.serviceAreaNameisMissing, true)
      return
    }
    this.props.onCompleteStep(2)
    this.props.onSaveOrganizationLocation({
      ...this.props.organization,
      id: this.props.orgId,
      serviceAreaName: this.state.serviceAreaName,
      serviceAreaBounds: this.state.serviceAreaBounds
    })
  }

  private onMapMounted = (map: any) => {
    //if (this.props.organization && this.props.organization.serviceArea) {
    //  this.drawOnMap(map, this.props.organization.serviceArea)
    //}
    this.setState({
      map
    })
  }

  private clear = () => {
    this.props.showMessage('', false)
    this.setState({ autoCompleteLocations: [] })
  }

  private onAddressChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: address } = event.target
    this.setState({ serviceAreaName: address })
    this.debounceAddressChange(address)
  }

  private fetchPolygon = (q: string) => {
    return new Promise((resolve, reject) => {
      fetch(encodeURI('https://nominatim.openstreetmap.org/search?q=' + q + '&polygon_geojson=1&format=json'))
        .then(response => {
          resolve(response.json())
        })
        .catch(result => {
          console.log('Failed to fetch polygon from openstreetmap: ' + result)
          this.props.showMessage('Failed to fetch polygon from openstreetmap: ' + result, true)
          reject(result)
        })
    })
  }

  private onLocationSelect = (selectedLocation: ILocation) => async () => {
    const { fullAddress } = selectedLocation
    this.clear()
    this.fetchPolygon(fullAddress).then(
      (json: any) => {
        this.clearMap()
        this.clearBuffer()
        const first = json[0]
        if (!first) {
          // TODO: throw error
          console.log("Unable to find '" + fullAddress + "'")
          this.props.showMessage('Please enter State,City or County', true)
          return
        }
        this.state.map.setCenter({
          lat: Number(first.lat),
          lng: Number(first.lon)
        })
        this.drawOnMap(this.state.map, first.geojson)
        this.setState({
          serviceAreaName: fullAddress,
          serviceAreaBounds: first.boundingbox
        })
        // automatically zoom the map base on the address coordinates
        const bounds = new window.google.maps.LatLngBounds()
        if (!!first.geojson.coordinates[0].length) {
          first.geojson.coordinates[0].forEach((coordinate: number[]) => {
            const latLng = { lat: coordinate[1], lng: coordinate[0] }
            bounds.extend(latLng)
          })
        }
        this.state.map.fitBounds(bounds)
      },
      err => {
        // TODO: Handle error calling this service
        return
      }
    )
  }

  private clearMap = () => {
    if (this.state.buffer.mapObject) {
      this.state.buffer.mapObject.setMap(null)
    }
  }

  private clearBuffer = () => {
    const buffer = { ...this.state.buffer }
    if (!buffer.mapObject) {
      return
    }
    buffer.mapObject.setMap(null)
    this.setState({
      buffer
    })
  }

  private setBuffer = (buffer: any) => {
    this.clearBuffer()
    this.setState({
      buffer
    })
  }

  private drawOnMap = (map: any, geojson: any) => {
    console.log(map, geojson)
    const coors = {
      type: 'FeatureCollection',
      features: [
        {
          type: 'Feature',
          geometry: Object.assign({}, geojson)
        }
      ]
    }

    const data = new google.maps.Data()
    data.setStyle({
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillColor: '#D67576',
      fillOpacity: 0.35
    })
    data.addGeoJson(coors)
    data.setMap(map)
    this.setBuffer({
      mapObject: data
    })
  }
}

export default OrganizationLocation



// WEBPACK FOOTER //
// ./src/components/organizations/location/OrganizationLocation.tsx